import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BILL_STATUS_MAP,
  RECURRENCE_CYCLE_MAP,
  RECURRING_STATUS_MAP,
  type BillStatus,
  type RecurrenceCycle,
  type RecurringStatus,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { date, money, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, EmptyState, ErrorState, PageHead, Stat } from '../../components/ui';

interface Run {
  id: string;
  periodStart: string;
  periodEnd: string;
  error: string | null;
  bill: {
    id: string;
    number: string;
    status: BillStatus;
    dueAt: string;
    total: string;
    paidAmount: string;
  } | null;
}

interface Detail {
  id: string;
  title: string;
  status: RecurringStatus;
  cycle: RecurrenceCycle;
  startDate: string;
  endDate: string | null;
  maxRuns: number | null;
  nextRunAt: string;
  lastRunAt: string | null;
  dayOfMonth: number;
  dueDays: number | null;
  autoReceive: boolean;
  periodTotal: number;
  issuedCount: number;
  taxRate: string;
  notes: string | null;
  supplier: { id: string; name: string; paymentTermDays: number };
  lines: Array<{
    id: string;
    description: string;
    quantity: string;
    unitPrice: string;
    account: { id: string; code: string; name: string };
  }>;
  runs: Run[];
}

export function RecurringBillDetail() {
  const { id = '' } = useParams();
  const { atLeast } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const canManage = atLeast('MANAGER');

  const detail = useQuery({
    queryKey: ['recurring-bills', id],
    queryFn: () => api<Detail>(`/finance/recurring-bills/${id}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['recurring-bills'] });
    void queryClient.invalidateQueries({ queryKey: ['bills'] });
  };

  const run = useMutation({
    mutationFn: () =>
      api<{ issued: number; skipped: number }>(`/finance/recurring-bills/${id}/run`, {
        method: 'POST',
      }),
    onSuccess: refresh,
  });

  const setStatus = useMutation({
    mutationFn: (status: RecurringStatus) =>
      api(`/finance/recurring-bills/${id}/status`, { method: 'PATCH', body: { status } }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: () => api(`/finance/recurring-bills/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      refresh();
      navigate('/finance/recurring-bills');
    },
  });

  if (detail.isError) {
    return <ErrorState error={detail.error} onRetry={() => detail.refetch()} />;
  }

  const r = detail.data;
  const today = new Date().toISOString().slice(0, 10);
  const isDue = r?.status === 'ACTIVE' && r.nextRunAt <= today;
  const failure = [run.error, setStatus.error, remove.error].find(Boolean);

  return (
    <div className="page">
      <PageHead
        title={r?.title ?? 'اشتراك مورد دوري'}
        subtitle={r ? `${r.supplier.name} — ${RECURRENCE_CYCLE_MAP[r.cycle].label}` : undefined}
        actions={
          r &&
          canManage && (
            <>
              <Link to="/finance/recurring-bills" className="btn">
                رجوع
              </Link>
              <Link to={`/finance/recurring-bills/${id}/edit`} className="btn">
                تعديل
              </Link>

              {r.status === 'ACTIVE' && (
                <>
                  <button className="btn" onClick={() => setStatus.mutate('PAUSED')}>
                    إيقاف مؤقت
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => run.mutate()}
                    disabled={run.isPending}
                  >
                    {run.isPending ? 'جارٍ الإصدار' : 'إصدار الآن'}
                  </button>
                </>
              )}

              {r.status === 'PAUSED' && (
                <button className="btn btn-primary" onClick={() => setStatus.mutate('ACTIVE')}>
                  استئناف
                </button>
              )}

              {r.status !== 'ENDED' && (
                <button className="btn btn-danger" onClick={() => remove.mutate()}>
                  إنهاء
                </button>
              )}
            </>
          )
        }
      />

      {run.isSuccess && (
        <div className="alert alert-info mb-4">
          {run.data.issued > 0
            ? `سُجّلت ${run.data.issued} فاتورة مورد.`
            : 'لا دورات مستحقة الآن — كل ما فات سُجّل بالفعل.'}
        </div>
      )}

      {failure && (
        <div className="alert alert-danger mb-4">
          {failure instanceof Error ? failure.message : 'تعذّرت العملية.'}
        </div>
      )}

      {r && (
        <>
          <div className="stats">
            <Stat
              label="الحالة"
              value={
                <Badge kind={RECURRING_STATUS_MAP[r.status].kind}>
                  {RECURRING_STATUS_MAP[r.status].label}
                </Badge>
              }
            />
            <Stat label="قيمة الدورة" value={<span className="num">{money(r.periodTotal)}</span>} />
            <Stat
              label="الإصدار التالي"
              value={
                r.status === 'ENDED' ? (
                  '—'
                ) : (
                  <span className={`num ${isDue ? 'neg' : ''}`}>{date(r.nextRunAt)}</span>
                )
              }
            />
            <Stat
              label="فواتير سُجّلت"
              value={`${r.runs.filter((x) => x.bill).length}${r.maxRuns ? ` / ${r.maxRuns}` : ''}`}
            />
          </div>

          {r.status === 'PAUSED' && (
            <div className="alert alert-warning mb-4">
              الاشتراك موقوف مؤقتًا فلا تُسجَّل منه فواتير. عند الاستئناف يبدأ من الدورة القادمة
              ولا يُسجّل ما فات أثناء التوقف.
            </div>
          )}

          <div className="builder">
            <div className="card">
              <div className="card-head">
                <h2 className="card-title">البنود</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الحساب</th>
                      <th>البيان</th>
                      <th>الكمية</th>
                      <th>السعر</th>
                      <th>الإجمالي</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.lines.map((l) => (
                      <tr key={l.id}>
                        <td className="cell-mute">
                          <span className="num">{l.account.code}</span> — {l.account.name}
                        </td>
                        <td className="cell-strong">{l.description}</td>
                        <td className="num cell-mute">{Number(l.quantity)}</td>
                        <td className="num">{money(l.unitPrice)}</td>
                        <td className="num">{money(Number(l.quantity) * Number(l.unitPrice))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2 className="card-title">الجدولة</h2>
              </div>
              <div className="card-body">
                <div className="totals-box">
                  <div className="totals-row">
                    <span className="mute">الدورة</span>
                    <span>{RECURRENCE_CYCLE_MAP[r.cycle].label}</span>
                  </div>
                  <div className="totals-row">
                    <span className="mute">يوم الإصدار</span>
                    <span className="num">{r.dayOfMonth}</span>
                  </div>
                  <div className="totals-row">
                    <span className="mute">من</span>
                    <span className="num">{date(r.startDate)}</span>
                  </div>
                  <div className="totals-row">
                    <span className="mute">إلى</span>
                    <span className="num">
                      {r.endDate
                        ? date(r.endDate)
                        : r.maxRuns
                          ? `بعد ${r.maxRuns} فاتورة`
                          : 'مفتوح'}
                    </span>
                  </div>
                  <div className="totals-row">
                    <span className="mute">مهلة السداد</span>
                    <span className="num">
                      {r.dueDays === null
                        ? `مهلة المورد (${plural(r.supplier.paymentTermDays, WORDS.day)})`
                        : plural(r.dueDays, WORDS.day)}
                    </span>
                  </div>
                  <div className="totals-row totals-final">
                    <span>وضع الإصدار</span>
                    <span>{r.autoReceive ? 'استلام وترحيل تلقائي' : 'مسودة للمراجعة'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mt-4">
            <div className="card-head">
              <h2 className="card-title">الفواتير المسجّلة</h2>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>الدورة</th>
                    <th>الفاتورة</th>
                    <th>الاستحقاق</th>
                    <th>القيمة</th>
                    <th>المسدَّد</th>
                    <th>الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {r.runs.map((x) => (
                    <tr key={x.id}>
                      <td className="num cell-mute">
                        {date(x.periodStart)} — {date(x.periodEnd)}
                      </td>
                      <td>
                        {x.bill ? (
                          /* فواتير الموردين تُفتح في نافذة من قائمتها، فالرابط بحثٌ برقمها. */
                          <Link
                            to={`/finance/bills?q=${encodeURIComponent(x.bill.number)}`}
                            className="cell-strong link num"
                          >
                            {x.bill.number}
                          </Link>
                        ) : (
                          <span className="cell-mute">{x.error ?? 'لم تُنشأ'}</span>
                        )}
                      </td>
                      <td className="num cell-mute">{x.bill ? date(x.bill.dueAt) : '—'}</td>
                      <td className="num">{x.bill ? money(x.bill.total) : '—'}</td>
                      <td className="num cell-mute">
                        {x.bill ? money(x.bill.paidAmount) : '—'}
                      </td>
                      <td>
                        {x.bill && (
                          <Badge kind={BILL_STATUS_MAP[x.bill.status].kind}>
                            {BILL_STATUS_MAP[x.bill.status].label}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {r.runs.length === 0 && (
              <EmptyState
                title="لم تُسجَّل دورات بعد"
                text="أول فاتورة تُسجَّل في موعدها، أو بضغط إصدار الآن."
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
